import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import { Truck, Gauge, AlertTriangle, ArrowLeft } from "lucide-react";

export default function VehicleStats() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/vehicles/${id}/diesel-stats`);
        setData(data);
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="text-sm text-gray-500">Loading…</div>;
  if (!data) return <div>Vehicle not found</div>;

  const v = data.vehicle;
  const lt = data.lifetime;
  const expected = data.expected_avg_kmpl;

  const statusColor = (flag) =>
    flag === "low" ? "text-[#B91C1C]" :
    flag === "high" ? "text-[#15803D]" : "text-gray-500";

  return (
    <div className="space-y-6" data-testid="vehicle-stats-page">
      <Link to="/vehicles" className="text-sm font-semibold text-gray-500 hover:text-black inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Back to vehicles
      </Link>

      <div className="card-flat p-5 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Truck size={28} strokeWidth={2.5} />
          <div>
            <div className="small-label text-gray-500">Vehicle</div>
            <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">{v.number}</h2>
            <div className="text-sm text-gray-600">{v.name || "—"}{v.notes ? ` · ${v.notes}` : ""}</div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div>
            <div className="small-label text-gray-500">Expected</div>
            <div className="data-num text-2xl">{formatMoney(expected)} kmpl</div>
          </div>
          <div>
            <div className="small-label text-gray-500">Lifetime Avg</div>
            <div className={`data-num text-2xl ${lt.deviation_pct !== null && Math.abs(lt.deviation_pct) >= data.deviation_threshold_pct ? (lt.deviation_pct < 0 ? "text-[#B91C1C]" : "text-[#15803D]") : ""}`}
              data-testid="lifetime-avg">
              {formatMoney(lt.avg_kmpl)} kmpl
            </div>
            {lt.deviation_pct !== null && (
              <div className="text-xs font-bold mt-0.5" style={{ color: lt.deviation_pct < 0 ? "#B91C1C" : "#15803D" }}>
                {lt.deviation_pct > 0 ? "+" : ""}{lt.deviation_pct}%
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Total km tracked</div>
          <div className="data-num text-3xl">{formatMoney(lt.km)} km</div>
          <div className="text-xs text-gray-500 mt-1">From first to last reading</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Total diesel</div>
          <div className="data-num text-3xl">{formatMoney(lt.litres)} L</div>
          <div className="text-xs text-gray-500 mt-1">Excluding baseline fill</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Total fills</div>
          <div className="data-num text-3xl">{data.runs.length}</div>
        </div>
      </div>

      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 small-label text-gray-500 flex items-center justify-between">
          <span>Tank-to-tank fills · newest first</span>
          <span className="text-xs text-gray-400 normal-case flex items-center gap-1">
            <AlertTriangle size={12} /> Deviation flagged at ±{data.deviation_threshold_pct}%
          </span>
        </div>
        <div className="overflow-auto">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="num">Odometer km</th>
                <th className="num">Km Run</th>
                <th className="num">Litres</th>
                <th className="num">Amount</th>
                <th className="num">Avg kmpl</th>
                <th className="num">Deviation</th>
              </tr>
            </thead>
            <tbody>
              {data.runs.length === 0 && (
                <tr><td colSpan="7" className="py-10 text-center text-gray-400 text-sm">
                  No diesel entries yet. Record one from <Link className="underline" to="/expenses">Expenses</Link>.
                </td></tr>
              )}
              {data.runs.map((r, idx) => (
                <tr key={r.expense_id} data-testid={`run-${r.expense_id}`}>
                  <td className="data-num text-xs">{r.date}</td>
                  <td className="num data-num">{formatMoney(r.odometer_km)}</td>
                  <td className="num data-num">{r.km_run === null ? <span className="text-gray-400">baseline</span> : formatMoney(r.km_run)}</td>
                  <td className="num data-num">{formatMoney(r.litres)}</td>
                  <td className="num data-num">₹ {formatMoney(r.amount)}</td>
                  <td className={`num data-num font-bold ${statusColor(r.flag)}`}>
                    {r.actual_avg_kmpl === null ? <span className="text-gray-400">—</span> : formatMoney(r.actual_avg_kmpl)}
                  </td>
                  <td className="num">
                    {r.deviation_pct === null ? <span className="text-gray-400">—</span> : (
                      <span className={`font-bold text-xs px-2 py-0.5 rounded-sm ${r.flag === "low" ? "stock-mt" : r.flag === "high" ? "stock-filled" : "text-gray-500"}`}>
                        {r.deviation_pct > 0 ? "+" : ""}{r.deviation_pct}%
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
